import { useState } from 'react'
import { Button, Form, Input, Select, InputNumber, Tag, Switch, App } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { serviceMastersApi } from '@/features/procurement/api'
import type { ServiceMaster } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'

const CATEGORIES = ['CIVIL', 'ELECTRICAL', 'MECHANICAL', 'IT', 'HOUSEKEEPING', 'TRANSPORT', 'OTHER']
const UOMS = ['HR', 'DAY', 'LUMPSUM', 'UNIT', 'KG', 'MTR', 'SQM']

export default function ServiceMasterPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['service-masters'],
    queryFn: () => serviceMastersApi.list(),
  })

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editId ? serviceMastersApi.update(editId, values) : serviceMastersApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-masters'] })
      message.success(editId ? 'Updated' : 'Created')
      setDrawerOpen(false)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const openCreate = () => {
    setEditId(null)
    form.resetFields()
    form.setFieldsValue({ gst_rate_pct: 18, is_active: true })
    setDrawerOpen(true)
  }

  const openEdit = (r: ServiceMaster) => {
    setEditId(r.id)
    form.setFieldsValue(r)
    setDrawerOpen(true)
  }

  const columns = [
    { title: 'Code', dataIndex: 'service_code', key: 'service_code', width: 120 },
    { title: 'Name', dataIndex: 'service_name', key: 'service_name' },
    { title: 'Category', dataIndex: 'service_category', key: 'service_category', width: 130 },
    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 90 },
    { title: 'GST %', dataIndex: 'gst_rate_pct', key: 'gst_rate_pct', width: 80 },
    { title: 'HSN/SAC', dataIndex: 'hsn_sac_code', key: 'hsn_sac_code', width: 100 },
    {
      title: 'Active', dataIndex: 'is_active', key: 'is_active', width: 80,
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: '', key: 'edit', width: 60,
      render: (_: unknown, r: ServiceMaster) => <a onClick={() => openEdit(r)}>Edit</a>,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Service Master"
        subtitle="Manage service catalog for Work Orders"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Service</Button>}
      />

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title={editId ? 'Edit Service' : 'New Service'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => saveMutation.mutate(v))}
        loading={saveMutation.isPending}
        form={form}
      >
        <Form.Item name="service_code" label="Service Code" rules={[{ required: true }]}>
          <Input disabled={!!editId} />
        </Form.Item>
        <Form.Item name="service_name" label="Service Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="service_category" label="Category" rules={[{ required: true }]}>
          <Select options={CATEGORIES.map(c => ({ label: c, value: c }))} />
        </Form.Item>
        <Form.Item name="uom" label="UOM" rules={[{ required: true }]}>
          <Select options={UOMS.map(u => ({ label: u, value: u }))} />
        </Form.Item>
        <Form.Item name="hsn_sac_code" label="HSN/SAC Code">
          <Input />
        </Form.Item>
        <Form.Item name="gst_rate_pct" label="GST Rate %" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={0} max={28} />
        </Form.Item>
        <Form.Item name="tds_section" label="TDS Section">
          <Input placeholder="e.g. 194C" />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
